import { create } from "zustand";
import type { PendingAttachment } from "./useAttachments";

/**
 * Unsent messages in existing chats' composers, one per chat.
 *
 * Switching to another chat or task unmounts the composer; coming back has to
 * find the message as it was left — text and attachments. The text is kept here
 * live, the attachments are parked when the composer unmounts. The agent is not:
 * an existing chat re-seeds from its own provider.
 */
export type ChatComposerDraft = {
  text: string;
  /** Attachments parked by an unmounted composer. Their preview URLs stay live until sent or dropped. */
  attachments: PendingAttachment[];
};

type ChatComposersState = {
  composers: Record<string, ChatComposerDraft>;
  setText: (chatId: string, text: string) => void;
  /** Keeps what an unmounting composer held. Returns false when there is nothing to keep, leaving the caller to release it. */
  park: (chatId: string, attachments: PendingAttachment[]) => boolean;
  /** A send from this chat landed — possibly after its composer left — so the message it held goes. */
  sent: (chatId: string, text: string, attachmentKeys: string[]) => void;
  reset: () => void;
};

function withComposer(composers: Record<string, ChatComposerDraft>, chatId: string, next: ChatComposerDraft) {
  if (next.text === "" && next.attachments.length === 0) {
    const { [chatId]: _removed, ...rest } = composers;
    return rest;
  }
  return { ...composers, [chatId]: next };
}

export const useChatComposers = create<ChatComposersState>()((set, get) => ({
  composers: {},
  setText: (chatId, text) => set((state) => {
    const current = state.composers[chatId];
    if ((current?.text ?? "") === text) return state;
    return { composers: withComposer(state.composers, chatId, { attachments: [], ...current, text }) };
  }),
  park: (chatId, attachments) => {
    const current = get().composers[chatId];
    if (!current && attachments.length === 0) return false;
    set((state) => ({ composers: withComposer(state.composers, chatId, { text: current?.text ?? "", attachments }) }));
    return attachments.length > 0;
  },
  sent: (chatId, text, attachmentKeys) => set((state) => {
    const current = state.composers[chatId];
    if (!current) return state;
    const sentKeys = new Set(attachmentKeys);
    const kept = current.attachments.filter((attachment) => {
      if (!sentKeys.has(attachment.key)) return true;
      URL.revokeObjectURL(attachment.previewUrl);
      return false;
    });
    // Text typed since the send went out is a new message, not the one that landed.
    const remaining = current.text.trim() === text.trim() ? "" : current.text;
    return { composers: withComposer(state.composers, chatId, { text: remaining, attachments: kept }) };
  }),
  reset: () => set((state) => {
    for (const composer of Object.values(state.composers)) {
      for (const attachment of composer.attachments) URL.revokeObjectURL(attachment.previewUrl);
    }
    return { composers: {} };
  }),
}));
